<template>
  <div class="schedule-table">
    <template v-if="isLoading">
      <div class="schedule-table-loading">
        <BaseSpinner size="sm" />
      </div>
    </template>

    <template v-else>
      <div v-if="slots.length">
        <div class="schedule-table-header">
          <div
            class="schedule-table-header-arrow schedule-table-header-prev arrow-left"
          >
            <BaseIcon icon="chevron-left" size="sm" />
          </div>

          <ul class="schedule-table-header-days">
            <Swiper :options="swiperOptions">
              <SwiperSlide v-for="(day, index) in slotDays" :key="index">
                <li
                  class="schedule-table-header-days-item"
                  :class="{
                    active:
                      (index === 0 && !selectedSlotDay) ||
                      day.slot_date === selectedSlotDay,
                  }"
                  @click="selectSlotDay(day.slot_date)"
                >
                  <span>{{ slotDayDate(day.slot_date) }}</span>
                  <span>{{ slotWeekDay(day.slot_date) }}</span>
                </li>
              </SwiperSlide>
            </Swiper>
          </ul>

          <div
            class="schedule-table-header-arrow schedule-table-header-next arrow-right"
          >
            <BaseIcon icon="chevron-right" size="sm" />
          </div>
        </div>

        <div v-if="isSelectingSlotDay" class="schedule-table-loading">
          <BaseSpinner size="sm" />
        </div>

        <div v-else class="schedule-table-body">
          <template
            v-for="(slot) in listAvailableSlots(
              availableSlots[selectedSlotDay] ||
              availableSlots[Object.keys(availableSlots)[0]],
            )"
          >
            <!-- eslint-disable-next-line -->
            <button
              type="button"
              class="schedule-table-btn"
              :class="selectedSlot.hour === slot.hour ? 'schedule-table-btn-active' : ''"
              @click="selectSlot(slot)"
            >
              {{ slot.hour }}
            </button>
          </template>
        </div>

        <div v-if="!isSelectingSlotDay" class="schedule-table-footer">
          <BaseButton variant="link" @click="showMore = !showMore">
            Ver todos os horários
            <BaseIcon
              :icon="showMore ? 'chevron-up' : 'chevron-down'"
              size="xs"
            />
          </BaseButton>
        </div>
      </div>

      <div v-else class="schedule-table-empty">
        <h6 class="schedule-table-empty-title">Sem horários disponíveis</h6>
        <p v-if="requestNewSlots" class="schedule-table-empty-text">
          Solicite agora novos horários para <br />
          esse profissional.
        </p>
        <p v-else class="schedule-table-empty-text">
          Escolha outro profissional com horários disponíveis.
        </p>
        <BaseButton
          v-if="requestNewSlots"
          size="xl"
          @click="requestNewSlots"
        >
          Solicitar
        </BaseButton>
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import { formatDate, formatWeekDay } from '../../utils/masks/formatSlotDate';

export default {
  name: 'ScheduleTable',
  props: {
    entity: {
      type: Object,
      default: () => {},
    },
    requestNewSlots: {
      type: Function,
      required: true,
    },
    unitId: {
      type: String,
      default: '',
    },
    onlySelectSlot: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      error: null,
      isLoading: false,
      isSelectingSlotDay: false,
      showMore: false,
      slotDaysCurrentPage: 0,
      slots: [],
      selectedSlotDay: '',
      selectedSlot: {},
    };
  },
  mounted() {
    if (this.entity.id) {
      this.handleFetchDoctorsAvailables();
    }
  },
  methods: {
    ...mapActions('user', ['showUser']),
    ...mapActions('doctor', ['fetchDoctorsAvailables', 'sendSMStoDoctor']),
    ...mapActions('appointments', ['holdScheduleAppointment']),
    ...mapActions('slot', ['setSlotsRequested', 'setSlotsRequestedDoctorId']),

    async handleFetchDoctorsAvailables() {
      try {
        this.isLoading = true;
        const response = await this.fetchDoctorsAvailables({
          id: this.entity.id,
          query: this.unitId ? `unit_id=${this.unitId}` : ''
        });
        this.slots = response.data.filter((availableSlot) => availableSlot.id);
      } catch (err) {
        this.error = err;
      } finally {
        this.isLoading = false;
      }
    },

    slotWeekDay(value) {
      return formatWeekDay(value);
    },
    slotDayDate(value) {
      return formatDate(value);
    },
    availableSlotHour(hour) {
      return !this.showMore ? hour.slice(0, 4) : hour;
    },
    selectSlotDay(date) {
      this.isSelectingSlotDay = true;
      this.selectedSlotDay = date;
      setTimeout(() => {
        this.isSelectingSlotDay = false;
      }, 400);
    },
    listAvailableSlots(slots) {
      return this.showMore ? slots : slots.slice(0, 15);
    },
    scheduleSlot(hour, date, id) {
      this.holdScheduleAppointment({
        scheduled_at: `${date} ${hour}:00`,
        scheduled_until: `${date} 00:00:00`,
        doctor: this.entity,
        slot_date: date,
        slot_hour: hour,
        slot_id: id,
      });
      if (!this.loggedIn) {
        this.$bvModal.show('signinModal');
      } else {
        this.$router.push({ name: 'Schedule' });
      }
    },

    selectSlot(slot) {
      if (this.onlySelectSlot) {
        this.$emit('on-select-slot', slot)
        this.selectedSlot = slot;
      } else {
        this.scheduleSlot(slot.hour, slot.slot_date, slot.slot_id)
      }
    },
  },
  computed: {
    ...mapGetters('auth', ['loggedIn']),
    ...mapGetters('user', ['getCurrentUser']),
    ...mapGetters('slot', ['slotsRequested', 'units']),

    availableSlots() {
      let slots = this.slots.reduce((prevValue, currentValue) => {
        prevValue[currentValue.slot_date] = [
          ...(prevValue[currentValue.slot_date] || []),
          ...currentValue.slots.map((slot) => ({
            slot_id: currentValue.id,
            slot_date: currentValue.slot_date,
            hour: slot,
          })),
        ];
        return prevValue;
      }, {});
      return slots;
    },
    slotDays() {
      let days = [];
      let slots = this.availableSlots;
      Object.keys(slots).filter((day) => {
        if (slots[day]) {
          days.push({
            slots: this.availableSlotHour(slots[day]),
            slot_date: day,
          });
        }
      });
      return days;
    },
    swiperOptions() {
      return {
        slidesPerView: 2,
        spaceBetween: 10,
        centeredSlides: false,
        loop: false,
        navigation: {
          nextEl: '.schedule-table-header-next',
          prevEl: '.schedule-table-header-prev',
        },
        breakpoints: {
          1200: {
            slidesPerView: 5,
            spaceBetween: 10,
          },
          992: {
            slidesPerView: 5,
            spaceBetween: 10,
          },
          768: {
            slidesPerView: 4,
            spaceBetween: 10,
          },
          360: {
            slidesPerView: 3,
            spaceBetween: 10,
          },
        },
      };
    },
    slotDaysMaxPage() {
      return Math.ceil(Object.keys(this.slotDays).length / 4) - 1;
    },
  },

  watch: {
    unitId(newValue) {
      if (newValue) {
        this.handleFetchDoctorsAvailables()
      }
    }
  }
};
</script>

<style lang="scss">
.swiper-container {
  width: 100%;
  display: flex;
  justify-content: space-between;
}
</style>
